import { CallState, LoadingState } from '@lcs/shared/api';
import { Dictionary, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { onReset } from 'src/app/authentication/reset-reducer';
import { Reports } from '../reports.enum';
import { ReportState } from './models/report-state';
import * as ReportActions from './report.actions';

export const reportsFeatureKey = 'reports';

const adapter = createEntityAdapter<ReportState<any, any>>();

export interface State extends EntityState<ReportState<any, any>> {
   currentReportId: Reports | null;
}

const initialState: State = adapter.getInitialState({ currentReportId: null });

export const reducer = createReducer(
   initialState,
   onReset(initialState),
   on(ReportActions.dashboardPageEnter, (state, action) => {
      const entities = {};
      action.tiles.forEach((t) => {
         entities[t.id] = {
            allowExcel: t.allowExcel ?? false,
            allowHtml: t.allowHtml ?? false,
            allowMemorize: t.allowMemorize ?? false,
            allowPdf: t.allowPdf ?? false,
            callState: LoadingState.Init,
            data: null,
            filename: '',
            id: t.id,
            name: t.name,
            parameters: null,
            rawParametersFormValue: null,
            url: '',
         };
      });
      return {
         ...state,
         currentReportId: null,
         entities,
         ids: Object.keys(entities),
      };
   }),
   on(ReportActions.pageEnter, (state, action) => {
      // When a report page is activated, we upsert some report configuration data
      // in the store that are needed to drive the generic report effects.
      return adapter.upsertOne(
         {
            allowExcel: action.allowExcel ?? true,
            allowHtml: action.allowHtml ?? true,
            allowMemorize: action.allowMemorize ?? false,
            allowPdf: action.allowPdf ?? true,
            callState: LoadingState.Init,
            contextHelpIds: action.contextHelpIds,
            contextualActionsCallState: LoadingState.Init,
            data: null,
            filename: action.filename,
            id: action.id,
            memorizedReportId: action.memorizedReportId,
            name: action.name,
            parameters: null,
            rawParametersFormValue: null,
            saveUrl: action.saveUrl,
            url: action.url,
         },
         { ...state, currentReportId: action.id }
      );
   }),
   on(ReportActions.reportParametersSubmitted, (state, action) => {
      // When a report parameters form is submitted we hold the parameters in the store
      // so that we can reuse them later for re-submit.
      return adapter.updateOne(
         {
            changes: {
               callState: LoadingState.Loading,
               parameters: action.reportParameters,
               rawParametersFormValue: action.rawFormValue,
            },
            id: action.id,
         },
         state
      );
   }),
   on(ReportActions.viewModeChanged, (state, action) => {
      return adapter.mapOne(
         {
            id: action.id,
            map: (report) => {
               return {
                  ...report,
                  data: {
                     ...report.data,
                     parameters: {
                        ...report.data.parameters,
                        viewMode: {
                           ...report.data.parameters.viewMode,
                           value: action.viewMode,
                        },
                     },
                  },
                  parameters: {
                     ...report.parameters,
                     viewMode: {
                        ...report.parameters.viewMode,
                        value: action.viewMode,
                     },
                  },
               };
            },
         },
         state
      );
   }),
   on(ReportActions.pageEnterAndSubmit, (state, action) => {
      return adapter.upsertOne(
         {
            allowExcel: action.allowExcel ?? true,
            allowHtml: action.allowHtml ?? true,
            allowMemorize: action.allowMemorize ?? true,
            allowPdf: action.allowPdf ?? true,
            callState: LoadingState.Loading,
            contextHelpIds: action.contextHelpIds,
            contextualActionsCallState: LoadingState.Init,
            data: null,
            filename: action.filename,
            id: action.id,
            memorizedReportId: action.memorizedReportId,
            name: action.name,
            parameters: action.reportParameters,
            rawParametersFormValue: action.rawFormValue,
            saveUrl: action.saveUrl,
            url: action.url,
         },
         { ...state, currentReportId: action.id }
      );
   }),
   on(ReportActions.downloadExcel, ReportActions.downloadHtml, ReportActions.downloadPdf, (state, action) => {
      return adapter.updateOne(
         {
            changes: {
               contextualActionsCallState: LoadingState.Loading,
            },
            id: action.id,
         },
         state
      );
   }),
   on(ReportActions.reportLoadSuccess, (state, action) => {
      return adapter.updateOne(
         {
            changes: {
               callState: LoadingState.Loaded,
               data: action.reportData,
            },
            id: action.id,
         },
         state
      );
   }),
   on(ReportActions.reportLoadFailure, (state, action) => {
      return adapter.updateOne(
         {
            changes: {
               callState: { error: action.error },
            },
            id: action.id,
         },
         state
      );
   }),
   on(ReportActions.downloadSuccess, (state, action) => {
      return adapter.updateOne(
         {
            changes: {
               contextualActionsCallState: LoadingState.Loaded,
            },
            id: action.id,
         },
         state
      );
   }),
   on(ReportActions.downloadFailure, (state, action) => {
      return adapter.updateOne(
         {
            changes: {
               contextualActionsCallState: LoadingState.Init,
            },
            id: action.id,
         },
         state
      );
   }),
   on(ReportActions.pageExit, (state, action) => {
      return adapter.removeOne(action.id, state);
   })
);

const featureSelector = createFeatureSelector<State>(reportsFeatureKey);

export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors(featureSelector);

export const selectReport = createSelector(
   selectEntities,
   (entities: Dictionary<ReportState<any, any>>, report: Reports): ReportState<any, any> | null => {
      return entities[report] ?? null;
   }
);

export const selectReportCallState = createSelector(
   selectEntities,
   (entities: Dictionary<ReportState<any, any>>, report: Reports): CallState | null => {
      return entities[report]?.callState ?? null;
   }
);

export const selectReportData = createSelector(
   selectEntities,
   (entities: Dictionary<ReportState<any, any>>, report: Reports) => {
      return entities[report]?.data ?? null;
   }
);

export const selectReportName = createSelector(
   selectEntities,
   (entities: Dictionary<ReportState<any, any>>, report: Reports): string | null => {
      return entities[report]?.name ?? null;
   }
);

export const selectCurrentReportId = createSelector(featureSelector, (state) => {
   return state.currentReportId;
});

export const selectCurrentReport = createSelector(
   selectEntities,
   selectCurrentReportId,
   (entities: Dictionary<ReportState<any, any>>, id: Reports | null): ReportState<any, any> | null => {
      return id ? entities[id] ?? null : null;
   }
);

export const selectCurrentReportName = createSelector(selectCurrentReport, (report: ReportState<any, any> | null) => {
   return report?.name ?? '';
});

export const selectCurrentReportContextHelpIds = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.contextHelpIds ?? [];
   }
);

export const selectCurrentReportCallState = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.callState ?? LoadingState.Init;
   }
);

export const selectCurrentReportLoading = createSelector(selectCurrentReportCallState, (callState: CallState) => {
   return callState === LoadingState.Loading;
});

export const selectCurrentReportContextualActionsCallState = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.contextualActionsCallState ?? LoadingState.Init;
   }
);

export const selectCurrentReportContextualActionsLoading = createSelector(
   selectCurrentReportContextualActionsCallState,
   (callState: CallState) => {
      return callState === LoadingState.Loading;
   }
);

export const selectCurrentReportData = createSelector(selectCurrentReport, (report: ReportState<any, any> | null) => {
   return report?.data ?? null;
});

export const selectCurrentReportFormValue = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.rawParametersFormValue ?? null;
   }
);

export const selectCurrentReportAllowExcel = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.allowExcel ?? true;
   }
);

export const selectCurrentReportAllowHtml = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.allowHtml ?? true;
   }
);

export const selectCurrentReportAllowPdf = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.allowPdf ?? true;
   }
);

export const selectCurrentReportAllowMemorize = createSelector(
   selectCurrentReport,
   (report: ReportState<any, any> | null) => {
      return report?.allowMemorize ?? true;
   }
);
